import { StateObjectSelector } from "molstar/lib/mol-state";
import { Color } from "molstar/lib/mol-util/color";
import { Bundle } from "molstar/lib/mol-model/structure/structure/element/bundle";

export type CryptoBenchResult = {
    status: string;
    prediction: number[];
    clusters: number[];
    pockets: Pocket[];
    sequence: string[];
    residue_ids: string[];
    input_structure: string;
    task_id: string;
    file_hash: string;
    structure_name: string;
};

export type Pocket = {
    pocket_id: number;
    residue_ids: string[];
    prediction: number[];
    average_prediction: number;
};

export type TaskStatus = {
    status?: string;
    task_id?: string;
    result?: string | CryptoBenchResult;
    error?: string;
};

export type Point3D = {
    x: number;
    y: number;
    z: number;
};

/**
 * cc: https://github.com/scheuerv/molart/
 * This type is used to simplify work with current residue focused in Mol*.
 */
export type MolstarResidue = {
    authName: string;
    authSeqNumber: number;
    chain: {
        asymId: string;
        authAsymId: string;
        entity: {
            entityId: string;
            index: number;
        };
        index: number;
    };
    index: number;
    insCode: string;
    isHet: boolean;
    name: string;
    seqNumber: number;
};

export type AHoJStructure = {
    pdb_id: string;
    chains: string[];
    ligands: string[];
    rmsd: number;
    pocket_rmsd: number;
    sasa: number;
    structure_file: string;
    structure_file_url: string;
    target_chains: string[];
    uniprot_ids: string[];
};

export type AHoJResponse = {
    // if needed, more fields can be added here based on the AHoJ response
    job_id: string;
    done: boolean;
    queries: {
        found_apo: AHoJStructure[];
        found_holo: AHoJStructure[];
        found_alphafold: AHoJStructure[];
        [key: string]: unknown;
    }[];
    result_zip_url: string;
    [key: string]: unknown;
};

export type PolymerRepresentationType = "cartoon" | "ball-and-stick" | "molecular-surface" | "backbone";

export type PocketRepresentationType = "cartoon" | "ball-and-stick" | "molecular-surface" | "none";

export const PolymerRepresentationValues: Record<string, PolymerRepresentationType> = {
    Cartoon: "cartoon",
    BallAndStick: "ball-and-stick",
    MolecularSurface: "molecular-surface",
    Backbone: "backbone"
} as const;

export const PocketRepresentationValues: Record<string, PocketRepresentationType> = {
    Cartoon: "cartoon",
    BallAndStick: "ball-and-stick",
    MolecularSurface: "molecular-surface",
    None: "none"
} as const;

export type RepresentationWithRef<T extends PolymerRepresentationType | PocketRepresentationType> = {
    type: T;
    object: StateObjectSelector;
    transparentObjectRef?: string;
    overPaintRef?: string;
    id?: number;
};

export type LoadedStructure = {
    structure: StateObjectSelector;
    data: StateObjectSelector;
    polymerRepresentations: RepresentationWithRef<PolymerRepresentationType>[];
    pocketRepresentations: RepresentationWithRef<PocketRepresentationType>[];
    structureUrl: string;
    structureName: string;
    ahojStructure?: AHoJStructure;
};

export type TrajectoryTaskResult = {
    status: string;
    trajectory: string;
    trimmed_pdb: string;
};

export type OverPaintParams = {
    bundle: Bundle,
    color: Color,
    clear: boolean;
};

export type ChainData = {
    threshold: number;
    chainId: string;
    residueNums: number[];
};